<script setup lang="ts">
import type { VNode } from "vue";
import { computed, nextTick, onMounted, ref, watch } from "vue";
import { clone, debounce, throttle } from "lodash-es";
import type { IDisplayShop, IShopSelectListProps } from "../types";
import {
  SHOPS_DETAILED_LIST_ITEMS_OFFSET,
  SHOPS_DETAILED_LIST_ITEMS_PER_SLICE,
  SHOPS_DETAILED_LIST_SCROLL_DIFF,
  SHOPS_LIST_SCROLL_THROTTLE_TIME,
} from "../constants";
import { ShopSelectDisplayField } from "../enums";
import ShopSelectListItem from "./ShopSelectListItem.vue";

const props = withDefaults(defineProps<IShopSelectListProps>(), {
  open: false,
  itemsOffset: SHOPS_DETAILED_LIST_ITEMS_OFFSET,
  itemsPerSlice: SHOPS_DETAILED_LIST_ITEMS_PER_SLICE,
  scrollDiff: SHOPS_DETAILED_LIST_SCROLL_DIFF,
  scrollThrottleTime: SHOPS_LIST_SCROLL_THROTTLE_TIME,
  displayFields: () => [
    ShopSelectDisplayField.StoreType,
    ShopSelectDisplayField.Address,
  ],
});
const emit = defineEmits<{
  "select:shop": [shopCode: string];
  "submit:shop": [shopCode: string];
}>();
const defHeightMap = new Map<string, number>();
const totalHeightRef = ref<number | null>(null);

const getTopByIndex = (index = 0) => {
  let top = 0;
  const list = listRef.value;
  for (let i = 0; i < index; i += 1) {
    top += defHeightMap.get(list[i].code) || list[i].approxHeight;
  }
  return top;
};
const getSliceTargetIndex = (scrollTop = 0) => {
  let itemsHeight = 0;
  const list = listRef.value;
  for (let i = 0, len = listRef.value.length; i < len; i += 1) {
    if (itemsHeight >= scrollTop) return i;
    itemsHeight += defHeightMap.get(list[i].code) || list[i].approxHeight;
  }
  return listRef.value.length - 1;
};
const getTotalHeight = () => {
  return listRef.value.reduce((acc, entry) => {
    const val = defHeightMap.get(entry.code) || entry.approxHeight;
    return acc + val;
  }, 0);
};
const setTotalHeight = () => {
  if (!props.open) return;
  totalHeightRef.value = getTotalHeight();
};
const setTotalHeightDebounced = debounce(setTotalHeight, 100);
const updateHeightMap = () => {
  const list = displaySliceRef.value;
  for (let i = 0, len = list.length; i < len; i += 1) {
    const code = list[i].code;
    if (defHeightMap.get(code)) continue;
    const el = document.getElementById(`shop-list-item--${code}`);
    if (!el) continue;
    defHeightMap.set(code, el.offsetHeight);
  }
};
const getCurrentSlice = (scrollTop = 0) => {
  const targetIndex = getSliceTargetIndex(scrollTop);
  const limit = props.itemsOffset * 2 + props.itemsPerSlice;
  let slice: IDisplayShop[] = [];
  const start = targetIndex - props.itemsOffset;
  const end = targetIndex + props.itemsPerSlice + props.itemsOffset;
  const startIndex = start < 0 ? 0 : start;
  const endIndex = end > props.list.length ? props.list.length : end;
  const top = getTopByIndex(startIndex);
  slice =
    limit >= listRef.value.length
      ? listRef.value
      : listRef.value.slice(startIndex, endIndex);
  displaySliceTop.value = top;
  displaySliceStartIndex.value = startIndex;
  displaySliceScrollTop.value = scrollTop;
  return slice;
};

const listRef = ref(clone(props.list));
const listElementRef = ref<HTMLElement | null>(null);
const displaySliceTop = ref<number>(0);
const displaySliceStartIndex = ref<number>(0);
const displaySliceScrollTop = ref<number>(0);
const displaySliceRef = ref<IDisplayShop[]>(getCurrentSlice(0));
const listInnerStyle = computed(() => ({
  height: totalHeightRef.value ? `${totalHeightRef.value}px` : "auth",
}));
const listSliceStyle = computed(() => ({
  transform: `translateY(${displaySliceTop.value}px)`,
}));

const checkCurrentSlice = throttle(
  () => {
    const scrollTop = listElementRef.value?.scrollTop;
    if (scrollTop === undefined || scrollTop === displaySliceScrollTop.value)
      return;
    displaySliceRef.value = getCurrentSlice(scrollTop);
  },
  props.scrollThrottleTime,
  { leading: false, trailing: true },
);

const handleStoreClick = (shopCode: string) => {
  emit("select:shop", shopCode);
};
const handleStoreSelectClick = (shopCode: string) => {
  emit("submit:shop", shopCode);
};
const handleScroll = () => {
  checkCurrentSlice();
};
const handleAddressMounted = (node: VNode, entry: IDisplayShop) => {
  if (!props.open || !node?.el || defHeightMap.get(entry.code)) return;
  defHeightMap.set(entry.code, node.el.offsetHeight);
  setTotalHeightDebounced();
};

onMounted(() => {
  setTotalHeight();
});

watch(
  () => props.open,
  (next) => {
    if (!next) return;
    nextTick(() => {
      updateHeightMap();
      setTotalHeight();
    });
  },
);
</script>

<template>
  <section class="shop-select__list__outer" data-test-id="shop-list">
    <div ref="listElementRef" class="shop-select__list" @scroll="handleScroll">
      <div class="shop-select__list__inner" :style="listInnerStyle">
        <section class="shop-select__list--slice" :style="listSliceStyle">
          <div
            v-for="single in displaySliceRef"
            :key="single.code"
            class="shop-select__list--item"
          >
            <ShopSelectListItem
              :id="`shop-list-item--${single.code}`"
              :address="single.address"
              :type="single.storeType"
              :opening-hours="single.openingHours"
              :closing-hours="single.closingHours"
              :title="single.title"
              :selected="single.selected"
              :display-fields="displayFields"
              :class="single.className"
              @click:store="() => handleStoreClick(single.code)"
              @click:select="() => handleStoreSelectClick(single.code)"
              @vue:mounted="(node: VNode) => handleAddressMounted(node, single)"
            />
          </div>
        </section>
      </div>
    </div>
  </section>
</template>

<style lang="postcss" scoped>
.shop-select__list {
  position: absolute;
  inset: 0;
  overflow: hidden auto;

  &__outer {
    height: 100%;
    position: relative;
  }

  &__inner {
    padding: 0;
  }

  &--slice {
    display: flex;
    flex-direction: column;
    padding: 0 var(--pl-unit-x5);

    @media (--pl-viewport-from-ml) {
      padding: unset;
      width: 320px;
      margin: 0 auto;
    }
  }
}
</style>
